/**
 * Table-switch cost attribution.
 *
 * PerfHud already answers "is a frame slow?" (worst-frame / FPS) and
 * utils/spikeRecorder answers "what script ran during that slow frame?" (LoAF).
 * Neither answers the question this ticket actually needs: **how much of that
 * belongs to switching tables?** Without it, "the switch still drops frames" and
 * "the app drops frames" are indistinguishable, and a fix can't be shown to work.
 *
 * This module closes that gap by bracketing each switch: when a switch starts we
 * remember the timestamp, and once the window has elapsed we ask the spike
 * recorder which long frames landed inside it and who ran during them. The result
 * is a per-switch record (spike count, worst blocking ms, top offender) plus a
 * session summary the HUD shows and the console can dump.
 *
 * Cost model matches the rest of the debug tooling: nothing runs unless the HUD
 * enables it (setSwitchProfilingEnabled), and even then the work is one timer per
 * switch — the spikes themselves are already being recorded.
 */

import { getSpikeSummary } from 'utils/spikeRecorder';

/**
 * How long after a switch begins to attribute long frames to it. Covers the
 * 260ms carousel slide plus the settle/hydration burst behind it.
 */
export const SWITCH_WINDOW_MS = 700;

/** Keep a session's worth of switches without growing unbounded. */
const MAX_PROFILES = 40;

export type SwitchProfile = {
  /** Date.now() when the switch started. */
  at: number;
  /** Long frames (LoAF/longtask) that landed inside the window. */
  spikes: number;
  /** Worst single blocking duration among them, ms. */
  worstMs: number;
  /** Summed blocking duration, ms — the switch's main-thread bill. */
  totalMs: number;
  /** Script with the most attributed ms in the window ('' when unattributed). */
  topOffender: string;
};

let enabled = false;
const profiles: SwitchProfile[] = [];
const pending = new Set<ReturnType<typeof setTimeout>>();

/** Turn attribution on/off. The HUD owns this; production stays at `false`. */
export const setSwitchProfilingEnabled = (next: boolean): void => {
  enabled = next;
  if (!next) {
    pending.forEach((timer) => clearTimeout(timer));
    pending.clear();
  }
};

export const isSwitchProfilingEnabled = (): boolean => enabled;

/**
 * Summarise the spikes recorded between `from` and `from + SWITCH_WINDOW_MS`.
 * Pure given the spike buffer — exported so the windowing is unit-testable.
 */
export const summarizeWindow = (
  from: number,
  spikes: Array<{
    at: number;
    blockingMs: number;
    scripts?: Array<{ source: string; functionName: string; durationMs: number }>;
  }>
): Omit<SwitchProfile, 'at'> => {
  const until = from + SWITCH_WINDOW_MS;
  const inWindow = spikes.filter((s) => s.at >= from && s.at <= until);
  const byScript = new Map<string, number>();
  let worstMs = 0;
  let totalMs = 0;
  inWindow.forEach((spike) => {
    worstMs = Math.max(worstMs, spike.blockingMs);
    totalMs += spike.blockingMs;
    (spike.scripts ?? []).forEach((script) => {
      const key = `${script.functionName || 'anonymous'}@${script.source}`;
      byScript.set(key, (byScript.get(key) ?? 0) + script.durationMs);
    });
  });
  let topOffender = '';
  let topMs = 0;
  byScript.forEach((ms, key) => {
    if (ms > topMs) {
      topMs = ms;
      topOffender = key;
    }
  });
  return {
    spikes: inWindow.length,
    worstMs: Math.round(worstMs),
    totalMs: Math.round(totalMs),
    topOffender
  };
};

/**
 * Mark the start of a table switch. Called from the one place every switch path
 * funnels through (components/RebindingTableView/tableSwitchTransition), so grid
 * taps, the table switcher, swipes and programmatic opens are all covered.
 */
export const noteTableSwitch = (): void => {
  if (!enabled) return;
  const at = Date.now();
  const timer = setTimeout(() => {
    pending.delete(timer);
    const profile: SwitchProfile = { at, ...summarizeWindow(at, getSpikeSummary().recent) };
    profiles.push(profile);
    while (profiles.length > MAX_PROFILES) profiles.shift();
  }, SWITCH_WINDOW_MS + 50);
  pending.add(timer);
};

export type SwitchProfileSummary = {
  /** Switches measured this session. */
  count: number;
  /** Worst blocking frame across every measured switch, ms. */
  worstMs: number;
  /** Mean main-thread bill per switch, ms. */
  avgTotalMs: number;
  /** Most recent switch, or null before the first one completes. */
  last: SwitchProfile | null;
};

export const getSwitchProfileSummary = (): SwitchProfileSummary => {
  if (profiles.length === 0) {
    return { count: 0, worstMs: 0, avgTotalMs: 0, last: null };
  }
  const worstMs = profiles.reduce((m, p) => Math.max(m, p.worstMs), 0);
  const totalMs = profiles.reduce((s, p) => s + p.totalMs, 0);
  return {
    count: profiles.length,
    worstMs,
    avgTotalMs: Math.round(totalMs / profiles.length),
    last: profiles[profiles.length - 1]
  };
};

export const getSwitchProfiles = (): SwitchProfile[] => profiles.slice();

/** Console dump for QA captures: one line per switch, worst offenders included. */
export const logSwitchProfiles = (): SwitchProfileSummary => {
  const summary = getSwitchProfileSummary();
  /* eslint-disable no-console */
  console.log(
    `[switchProfile] ${summary.count} switches · worst frame ${summary.worstMs}ms · avg ${summary.avgTotalMs}ms blocked per switch`
  );
  if (!getSpikeSummary().supported) {
    console.log('(no long-frame attribution on this browser — iOS/Safari lacks LoAF + longtask)');
  }
  console.table(
    profiles.map((p) => ({
      at: new Date(p.at).toISOString().slice(11, 23),
      spikes: p.spikes,
      worstMs: p.worstMs,
      blockedMs: p.totalMs,
      topOffender: p.topOffender
    }))
  );
  /* eslint-enable no-console */
  return summary;
};

/** Wipe measurements (wired to the HUD's reset). */
export const resetSwitchProfiles = (): void => {
  profiles.length = 0;
  pending.forEach((timer) => clearTimeout(timer));
  pending.clear();
};
